import React, { useCallback, useMemo, useState } from 'react'
import { Autocomplete, LoadScript } from '@react-google-maps/api'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    MenuItem,
    Paper,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material'
import { useTheme } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CheckIcon from '@material-ui/icons/Check'
import HomeWorkIcon from '@material-ui/icons/HomeWork'
import PersonIcon from '@material-ui/icons/Person'
import RefreshIcon from '@material-ui/icons/Refresh'

import { statesList } from '../../../helpers'
import { StyledFormContainer } from '../styles'
import { FormUsersSection, hasIncompleteFormUsers } from '../FormUsersSection'
import { FormUser } from '../../../models/Forms'
import {
    useSheetOnboard,
    SheetOnboardCreateRequest,
} from '../../../hooks/useSheetOnboard'
import {
    AreaConfigPreviewList,
    BuildingPreviewTree,
    UnitConfigPreviewList,
    countFolderTree,
} from './SheetOnboardPreviews'
import { SheetOnboardUpload } from './SheetOnboardUpload'
import {
    SHEET_ONBOARD_TEMPLATE_COLUMNS,
    downloadSheetOnboardTemplate,
} from './sheetOnboardTemplates'

interface Props {
    organization: number
}

const STEPS = [
    'Property',
    'Area Types',
    'Unit Types',
    'Buildings And Units',
    'Review',
]
const STEP_PROPERTY = 0
const STEP_AREAS = 1
const STEP_UNITS = 2
const STEP_BUILDING = 3
const STEP_REVIEW = 4

const emptyUser: FormUser = { name: '', email: '' }

const initialsFor = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export const SheetOnboardWizard = ({ organization }: Props) => {
    const theme = useTheme()
    const [activeStep, setActiveStep] = useState(STEP_PROPERTY)
    const [showValidation, setShowValidation] = useState(false)

    const [propertyName, setPropertyName] = useState('')
    const [address, setAddress] = useState('')
    const [city, setCity] = useState('')
    const [stateVal, setStateVal] = useState('')
    const [zipCode, setZipCode] = useState('')
    const [regionalManager, setRegionalManager] = useState<FormUser>(emptyUser)
    const [propertyManager, setPropertyManager] = useState<FormUser>(emptyUser)
    const [billingEmail, setBillingEmail] = useState('')
    const [otherUsers, setOtherUsers] = useState<FormUser[]>([])

    const [uploadErrors, setUploadErrors] = useState<string[]>([])
    const [uploadedFiles, setUploadedFiles] = useState<{
        area_sheet?: string
        unit_sheet?: string
        infrastructure_sheet?: string
    }>({})

    const {
        state,
        create,
        upload,
        resetSheets,
        complete,
        resetWizard,
        isCreating,
        isUploading,
        isResetting,
        isCompleting,
    } = useSheetOnboard()

    const usersValue = {
        regional_manager: regionalManager,
        property_manager: propertyManager,
        other_users: otherUsers,
    }

    const invitedUsers = useMemo(() => {
        const users: { role: string; name: string; email: string }[] = [
            {
                role: 'Regional Manager',
                name: regionalManager.name,
                email: regionalManager.email,
            },
        ]
        if (propertyManager.name && propertyManager.email) {
            users.push({
                role: 'Property Manager',
                name: propertyManager.name,
                email: propertyManager.email,
            })
        }
        otherUsers
            .filter((u) => u.name && u.email)
            .forEach((u) =>
                users.push({ role: 'User', name: u.name, email: u.email }),
            )
        return users
    }, [regionalManager, propertyManager, otherUsers])

    const canAdvanceProperty = () =>
        Boolean(
            propertyName.trim() &&
                address.trim() &&
                city.trim() &&
                stateVal.trim() &&
                zipCode.trim() &&
                billingEmail.trim() &&
                !hasIncompleteFormUsers(usersValue),
        )

    const handlePlaceSelect = (
        autocomplete: google.maps.places.Autocomplete,
    ) => {
        const place = autocomplete.getPlace()
        if (!place?.address_components) {
            return
        }
        const addressComponents = place.address_components
        const streetNumber =
            addressComponents.find((component) =>
                component.types.includes('street_number'),
            )?.long_name || ''
        const route =
            addressComponents.find((component) =>
                component.types.includes('route'),
            )?.long_name || ''
        const nextCity =
            addressComponents.find((component) =>
                component.types.includes('locality'),
            )?.long_name || ''
        const nextState =
            addressComponents.find((component) =>
                component.types.includes('administrative_area_level_1'),
            )?.short_name || ''
        const nextZip =
            addressComponents.find((component) =>
                component.types.includes('postal_code'),
            )?.short_name || ''

        setAddress(`${streetNumber} ${route}`.trim())
        setCity(nextCity)
        setStateVal(nextState)
        setZipCode(nextZip)
    }

    const handleCreateProperty = async () => {
        if (!canAdvanceProperty()) {
            setShowValidation(true)
            return
        }
        setShowValidation(false)

        const validOtherUsers = otherUsers.filter((u) => u.name && u.email)

        const req: SheetOnboardCreateRequest = {
            organization,
            property_name: propertyName.trim(),
            address: address.trim(),
            city: city.trim(),
            state: stateVal.trim().slice(0, 2),
            zip_code: zipCode.trim().slice(0, 10),
            billing_email: billingEmail.trim() || null,
            regional_manager: regionalManager,
            property_manager:
                propertyManager.name && propertyManager.email
                    ? propertyManager
                    : undefined,
            other_users: validOtherUsers.length ? validOtherUsers : undefined,
        }

        await create(req)
        setActiveStep(STEP_AREAS)
    }

    const handleUpload = useCallback(
        async (
            sheetType: 'area_sheet' | 'unit_sheet' | 'infrastructure_sheet',
            file: File,
        ) => {
            if (!state.apartmentId) return
            setUploadErrors([])
            const res = await upload({
                apartmentId: state.apartmentId,
                sheetType,
                file,
                organization,
            })
            setUploadedFiles((prev) => ({
                ...prev,
                [sheetType]: file.name,
            }))
            if (res?.errors?.length) {
                setUploadErrors(
                    res.errors.map((e: any) => e.message || JSON.stringify(e)),
                )
            }
        },
        [state.apartmentId, organization, upload],
    )

    const handleReset = async () => {
        if (!state.apartmentId) return
        setUploadErrors([])
        setUploadedFiles({})
        await resetSheets({ apartmentId: state.apartmentId, organization })
    }

    const handleComplete = async () => {
        if (!state.apartmentId) return
        await complete({ apartmentId: state.apartmentId, organization })
        resetWizard()
        setActiveStep(STEP_PROPERTY)
        setPropertyName('')
        setAddress('')
        setCity('')
        setStateVal('')
        setZipCode('')
        setBillingEmail('')
        setRegionalManager(emptyUser)
        setPropertyManager(emptyUser)
        setOtherUsers([])
        setUploadedFiles({})
    }

    const { preview } = state
    const { units: buildingUnitCount } = countFolderTree(preview.folders)

    const canAdvanceStep = () => {
        if (activeStep === STEP_AREAS) {
            return preview.areas.length > 0
        }
        if (activeStep === STEP_UNITS) {
            return preview.unit_configs.length > 0
        }
        if (activeStep === STEP_BUILDING) {
            return buildingUnitCount > 0
        }
        return true
    }

    const handleNext = () => {
        if (!canAdvanceStep()) {
            return
        }
        setUploadErrors([])
        setActiveStep((s) => Math.min(s + 1, STEP_REVIEW))
    }
    const handleBack = () => {
        setUploadErrors([])
        setActiveStep((s) => Math.max(s - 1, STEP_PROPERTY))
    }

    const stepCopy = [
        'Enter the property details and the people who should have access.',
        'Define the rooms and spaces that can appear inside a unit, like Bed, Bath, or Common Area.',
        'Describe each floor plan by saying how many of those spaces belong to it. A blank unit type continues the row above.',
        'Build the property: folders first, then DONE, then each unit and its unit type.',
        'Review everything and finish onboarding.',
    ]

    const stepReviewChip =
        activeStep === STEP_AREAS && preview.areas.length > 0
            ? `${preview.areas.length} area${
                  preview.areas.length === 1 ? '' : 's'
              } to review`
            : activeStep === STEP_UNITS && preview.unit_configs.length > 0
            ? `${preview.unit_configs.length} unit type${
                  preview.unit_configs.length === 1 ? '' : 's'
              } to review`
            : activeStep === STEP_BUILDING && buildingUnitCount > 0
            ? `${buildingUnitCount} unit${
                  buildingUnitCount === 1 ? '' : 's'
              } to review`
            : null

    const primaryButtonStyle: React.CSSProperties = {
        textTransform: 'none',
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        minWidth: 140,
        fontWeight: 600,
    }

    const cardStyle: React.CSSProperties = {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
    }

    const startTemplateDownload = downloadSheetOnboardTemplate

    return (
        <StyledFormContainer maxWidth="lg">
            <Box style={{ flexShrink: 0, marginBottom: theme.spacing(1) }}>
                <Typography
                    variant="h5"
                    style={{
                        fontWeight: 700,
                        color: theme.palette.darkBlue.main,
                    }}
                >
                    Onboard with Sheets
                </Typography>
                <Typography
                    variant="body2"
                    style={{
                        marginTop: 4,
                        color: theme.palette.text.secondary,
                    }}
                >
                    {stepCopy[activeStep]}
                </Typography>
            </Box>

            <Stepper
                activeStep={activeStep}
                alternativeLabel
                style={{
                    margin: `${theme.spacing(1)}px 0 ${theme.spacing(2)}px`,
                    flexShrink: 0,
                    backgroundColor: 'transparent',
                    padding: theme.spacing(1, 0, 0),
                }}
            >
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <div className="form-content">
                <div className="wizard-section">
                    {/* ---- STEP 0 : PROPERTY ---- */}
                    {activeStep === STEP_PROPERTY && (
                        <div className="wizard-list">
                            <Paper elevation={0} style={cardStyle}>
                                <Typography
                                    variant="subtitle2"
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: 12,
                                    }}
                                >
                                    Property Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Property Name"
                                            value={propertyName}
                                            onChange={(e) =>
                                                setPropertyName(e.target.value)
                                            }
                                            required
                                            error={
                                                showValidation &&
                                                !propertyName.trim()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <LoadScript
                                            googleMapsApiKey={`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                                            libraries={['places']}
                                        >
                                            <Autocomplete
                                                onLoad={(autocomplete) => {
                                                    autocomplete.addListener(
                                                        'place_changed',
                                                        () =>
                                                            handlePlaceSelect(
                                                                autocomplete,
                                                            ),
                                                    )
                                                }}
                                            >
                                                <div className="address-autocomplete">
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Street Address"
                                                        value={address}
                                                        onChange={(e) =>
                                                            setAddress(
                                                                e.target.value,
                                                            )
                                                        }
                                                        required
                                                        error={
                                                            showValidation &&
                                                            !address.trim()
                                                        }
                                                    />
                                                </div>
                                            </Autocomplete>
                                        </LoadScript>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="City"
                                            value={city}
                                            onChange={(e) =>
                                                setCity(e.target.value)
                                            }
                                            required
                                            error={
                                                showValidation && !city.trim()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            select
                                            label="State"
                                            value={stateVal}
                                            onChange={(e) =>
                                                setStateVal(e.target.value)
                                            }
                                            required
                                            error={
                                                showValidation &&
                                                !stateVal.trim()
                                            }
                                        >
                                            {statesList.map((state) => (
                                                <MenuItem
                                                    key={state}
                                                    value={state}
                                                >
                                                    {state}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="ZIP Code"
                                            value={zipCode}
                                            onChange={(e) =>
                                                setZipCode(e.target.value)
                                            }
                                            inputProps={{ maxLength: 10 }}
                                            required
                                            error={
                                                showValidation &&
                                                !zipCode.trim()
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Billing Email"
                                            type="email"
                                            value={billingEmail}
                                            onChange={(e) =>
                                                setBillingEmail(e.target.value)
                                            }
                                            required
                                            error={
                                                showValidation &&
                                                !billingEmail.trim()
                                            }
                                            helperText="Email for invoices and billing communications."
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper elevation={0} style={cardStyle}>
                                <Grid container spacing={2}>
                                    <FormUsersSection
                                        variant="wizard"
                                        value={usersValue}
                                        showValidation={showValidation}
                                        onManagerChange={(
                                            managerType,
                                            field,
                                            value,
                                        ) => {
                                            if (
                                                managerType ===
                                                'regional_manager'
                                            ) {
                                                setRegionalManager((prev) => ({
                                                    ...prev,
                                                    [field]: value,
                                                }))
                                            } else {
                                                setPropertyManager((prev) => ({
                                                    ...prev,
                                                    [field]: value,
                                                }))
                                            }
                                        }}
                                        onOtherUserChange={(
                                            index,
                                            field,
                                            value,
                                        ) =>
                                            setOtherUsers((prev) =>
                                                prev.map((u, i) =>
                                                    i === index
                                                        ? {
                                                              ...u,
                                                              [field]: value,
                                                          }
                                                        : u,
                                                ),
                                            )
                                        }
                                        onAddOtherUser={() =>
                                            setOtherUsers((prev) => [
                                                ...prev,
                                                { name: '', email: '' },
                                            ])
                                        }
                                        onRemoveOtherUser={(index) =>
                                            setOtherUsers((prev) =>
                                                prev.filter(
                                                    (_, i) => i !== index,
                                                ),
                                            )
                                        }
                                    />
                                </Grid>
                            </Paper>
                        </div>
                    )}

                    {/* ---- STEP 1 : AREAS ---- */}
                    {activeStep === STEP_AREAS && (
                        <div className="wizard-list">
                            <SheetOnboardUpload
                                title="Area types"
                                description="These are the kinds of rooms and spaces a unit can contain. Occupiable spaces are bedrooms. Bathrooms, kitchens, and other shared rooms are common."
                                columns={
                                    SHEET_ONBOARD_TEMPLATE_COLUMNS.area_sheet
                                }
                                tips={[
                                    'One row per area type. Name is required.',
                                    'Use 1 or TRUE for occupiable, 0 or FALSE for common.',
                                    'Use these exact names later on the unit types sheet.',
                                ]}
                                fileName={uploadedFiles.area_sheet}
                                uploadedCount={preview.areas.length}
                                uploadedLabel={
                                    preview.areas.length
                                        ? `${preview.areas.length} area type${
                                              preview.areas.length === 1
                                                  ? ''
                                                  : 's'
                                          }`
                                        : undefined
                                }
                                isUploading={isUploading}
                                onUpload={(file) =>
                                    handleUpload('area_sheet', file)
                                }
                                onDownloadTemplate={() =>
                                    startTemplateDownload('area_sheet')
                                }
                            />
                            {uploadErrors.length > 0 && (
                                <Alert
                                    severity="warning"
                                    style={{ marginBottom: theme.spacing(2) }}
                                >
                                    {uploadErrors.map((msg, i) => (
                                        <div key={i}>{msg}</div>
                                    ))}
                                </Alert>
                            )}
                            <AreaConfigPreviewList areas={preview.areas} />
                        </div>
                    )}

                    {/* ---- STEP 2 : UNITS ---- */}
                    {activeStep === STEP_UNITS && (
                        <div className="wizard-list">
                            <SheetOnboardUpload
                                title="Unit types"
                                description="A unit type is a floor plan. List how many of each area type belong to it. Leave Unit Type blank to keep adding areas to the unit type above."
                                columns={
                                    SHEET_ONBOARD_TEMPLATE_COLUMNS.unit_sheet
                                }
                                tips={[
                                    'Example: 2BR with Bed 2, then a blank row for Bath 1 and Common Area 1.',
                                    'Area names must match the area types you just uploaded.',
                                    'Amount is how many of that space are in the floor plan.',
                                ]}
                                fileName={uploadedFiles.unit_sheet}
                                uploadedCount={preview.unit_configs.length}
                                uploadedLabel={
                                    preview.unit_configs.length
                                        ? `${
                                              preview.unit_configs.length
                                          } unit type${
                                              preview.unit_configs.length === 1
                                                  ? ''
                                                  : 's'
                                          }`
                                        : undefined
                                }
                                disabled={preview.areas.length === 0}
                                disabledReason="Upload area types first"
                                isUploading={isUploading}
                                onUpload={(file) =>
                                    handleUpload('unit_sheet', file)
                                }
                                onDownloadTemplate={() =>
                                    startTemplateDownload('unit_sheet')
                                }
                            />
                            {uploadErrors.length > 0 && (
                                <Alert
                                    severity="warning"
                                    style={{ marginBottom: theme.spacing(2) }}
                                >
                                    {uploadErrors.map((msg, i) => (
                                        <div key={i}>{msg}</div>
                                    ))}
                                </Alert>
                            )}
                            <UnitConfigPreviewList
                                unitConfigs={preview.unit_configs}
                            />
                        </div>
                    )}

                    {/* ---- STEP 3 : BUILDING ---- */}
                    {activeStep === STEP_BUILDING && (
                        <div className="wizard-list">
                            <SheetOnboardUpload
                                title="Buildings and units"
                                description="This sheet creates the property layout. Columns before DONE become folders. After DONE, each row is a real unit and the unit type it uses."
                                columns={
                                    SHEET_ONBOARD_TEMPLATE_COLUMNS.infrastructure_sheet
                                }
                                tips={[
                                    'Example: Bldg A | 1 | DONE | 101 | 2BR creates unit 101 in Bldg A / 1.',
                                    'You can add more folder columns before DONE if you have stacks or halls.',
                                    'Unit Type must match a unit type from the previous step.',
                                ]}
                                fileName={uploadedFiles.infrastructure_sheet}
                                uploadedCount={buildingUnitCount}
                                uploadedLabel={
                                    buildingUnitCount
                                        ? `${buildingUnitCount} unit${
                                              buildingUnitCount === 1 ? '' : 's'
                                          }`
                                        : undefined
                                }
                                disabled={preview.unit_configs.length === 0}
                                disabledReason="Upload unit types first"
                                isUploading={isUploading}
                                onUpload={(file) =>
                                    handleUpload('infrastructure_sheet', file)
                                }
                                onDownloadTemplate={() =>
                                    startTemplateDownload(
                                        'infrastructure_sheet',
                                    )
                                }
                            />
                            {uploadErrors.length > 0 && (
                                <Alert
                                    severity="warning"
                                    style={{ marginBottom: theme.spacing(2) }}
                                >
                                    {uploadErrors.map((msg, i) => (
                                        <div key={i}>{msg}</div>
                                    ))}
                                </Alert>
                            )}
                            <BuildingPreviewTree folders={preview.folders} />
                        </div>
                    )}

                    {/* ---- STEP 4 : REVIEW ---- */}
                    {activeStep === STEP_REVIEW && (
                        <div className="wizard-list">
                            <Paper
                                elevation={0}
                                style={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                <Box
                                    style={{
                                        padding: theme.spacing(2.5, 3),
                                        backgroundColor: '#F7F9F8',
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        style={{
                                            fontWeight: 700,
                                            color: theme.palette.darkBlue.main,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        Ready to onboard
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ marginTop: 4 }}
                                    >
                                        Confirm the property, people, and
                                        configuration below.
                                    </Typography>
                                    <Box
                                        display="flex"
                                        style={{
                                            gap: 8,
                                            marginTop: 12,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            icon={
                                                <HomeWorkIcon
                                                    style={{ fontSize: 16 }}
                                                />
                                            }
                                            label={propertyName}
                                        />
                                        <Chip
                                            size="small"
                                            icon={
                                                <PersonIcon
                                                    style={{ fontSize: 16 }}
                                                />
                                            }
                                            label={
                                                invitedUsers.length === 1
                                                    ? '1 person invited'
                                                    : `${invitedUsers.length} people invited`
                                            }
                                        />
                                        {preview.areas.length > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${preview.areas.length} areas`}
                                                variant="outlined"
                                            />
                                        )}
                                        {preview.unit_configs.length > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${preview.unit_configs.length} unit types`}
                                                variant="outlined"
                                            />
                                        )}
                                        {preview.folders.length > 0 && (
                                            <Chip
                                                size="small"
                                                label="Infrastructure uploaded"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </Box>

                                <Grid container>
                                    <Grid
                                        item
                                        xs={12}
                                        md={7}
                                        style={{
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Box
                                            style={{
                                                padding: theme.spacing(2, 3, 1),
                                            }}
                                        >
                                            <Typography
                                                variant="overline"
                                                style={{
                                                    letterSpacing: 0.8,
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                }}
                                            >
                                                Property
                                            </Typography>
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="flex-start"
                                            style={{
                                                gap: 12,
                                                padding: theme.spacing(1.5, 3),
                                            }}
                                        >
                                            <Box
                                                style={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 8,
                                                    backgroundColor: '#E8F3F1',
                                                    color:
                                                        theme.palette.primary
                                                            .dark,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <HomeWorkIcon
                                                    style={{ fontSize: 18 }}
                                                />
                                            </Box>
                                            <Box minWidth={0}>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    {propertyName}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {[
                                                        address,
                                                        city,
                                                        stateVal,
                                                        zipCode,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(', ')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={5}>
                                        <Box
                                            style={{
                                                padding: theme.spacing(2, 3, 1),
                                            }}
                                        >
                                            <Typography
                                                variant="overline"
                                                style={{
                                                    letterSpacing: 0.8,
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                }}
                                            >
                                                People
                                            </Typography>
                                        </Box>
                                        {invitedUsers.map((user, index) => (
                                            <Box
                                                key={`${user.email}-${index}`}
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: 12,
                                                    padding: theme.spacing(
                                                        1.5,
                                                        3,
                                                    ),
                                                }}
                                            >
                                                <Box
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            theme.palette
                                                                .primary.dark,
                                                        color:
                                                            theme.palette
                                                                .primary
                                                                .contrastText,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {initialsFor(user.name)}
                                                </Box>
                                                <Box minWidth={0} flex={1}>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                        noWrap
                                                    >
                                                        {user.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        noWrap
                                                        display="block"
                                                    >
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    size="small"
                                                    label={user.role}
                                                    variant="outlined"
                                                />
                                            </Box>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Paper>
                            <Box style={{ marginTop: theme.spacing(2) }}>
                                <AreaConfigPreviewList areas={preview.areas} />
                                <UnitConfigPreviewList
                                    unitConfigs={preview.unit_configs}
                                />
                                <BuildingPreviewTree
                                    folders={preview.folders}
                                />
                            </Box>
                        </div>
                    )}

                    {/* ---- FOOTER ---- */}
                    <div className="wizard-footer">
                        <Button
                            onClick={
                                activeStep === STEP_PROPERTY
                                    ? undefined
                                    : handleBack
                            }
                            disabled={
                                activeStep === STEP_PROPERTY ||
                                isCreating ||
                                isCompleting
                            }
                            startIcon={<ArrowBackIcon />}
                            style={{ textTransform: 'none' }}
                        >
                            Back
                        </Button>

                        {activeStep > STEP_PROPERTY &&
                        activeStep < STEP_REVIEW ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 8 }}
                            >
                                {stepReviewChip ? (
                                    <Chip
                                        size="small"
                                        color="primary"
                                        label={stepReviewChip}
                                    />
                                ) : (
                                    <Chip
                                        size="small"
                                        label="Upload a sheet to review"
                                    />
                                )}
                                {state.apartmentId && (
                                    <Button
                                        size="small"
                                        color="secondary"
                                        startIcon={
                                            isResetting ? (
                                                <CircularProgress
                                                    size={14}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <RefreshIcon />
                                            )
                                        }
                                        onClick={handleReset}
                                        disabled={isResetting || isUploading}
                                        style={{ textTransform: 'none' }}
                                    >
                                        Reset Sheets
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <span />
                        )}

                        {activeStep === STEP_PROPERTY ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCreateProperty}
                                disabled={isCreating}
                                startIcon={
                                    isCreating ? (
                                        <CircularProgress
                                            size={16}
                                            color="inherit"
                                        />
                                    ) : undefined
                                }
                                style={primaryButtonStyle}
                            >
                                {isCreating ? 'Creating…' : 'Create & Continue'}
                            </Button>
                        ) : activeStep < STEP_REVIEW ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNext}
                                disabled={isUploading || !canAdvanceStep()}
                                style={primaryButtonStyle}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleComplete}
                                disabled={isCompleting}
                                startIcon={
                                    isCompleting ? (
                                        <CircularProgress
                                            size={16}
                                            color="inherit"
                                        />
                                    ) : (
                                        <CheckIcon />
                                    )
                                }
                                style={primaryButtonStyle}
                            >
                                {isCompleting
                                    ? 'Finishing…'
                                    : 'Confirm and onboard'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </StyledFormContainer>
    )
}
